/**
 * ModelManager - Central system for managing character model selection and loading
 *
 * This class serves as a single source of truth for which model is currently selected
 * and handles the communication with the server to update the current model.
 */

import { getLabUrlForModel } from "./characterSlugMapping.js";

export class ModelManager {
    constructor() {
        // Singleton pattern
        if (ModelManager.instance) {
            return ModelManager.instance;
        }

        this.currentModelId = null;
        this.isModelLoading = false;
        this.modelLoadListeners = [];
        this.modelSelectListeners = [];
        // Where currentModelId came from: 'localStorage' / 'preselect' (explicit,
        // per-visitor → trustworthy) vs 'server' (the process-global
        // app.config CURRENT_MODEL fallback, shared across ALL visitors → must
        // NOT auto-download for a genuine cold visitor). See ModelViewer.initialize.
        this.modelIdSource = null;

        // Initialize by checking server and localStorage
        this.initialize();

        ModelManager.instance = this;
    }

    /**
     * Initialize the ModelManager by checking both server-side and client-side storage
     */
    async initialize() {
        try {
            // Check if there's a preselected model from URL (via Flask template)
            const urlPreselectedModel = window.PRESELECTED_MODEL_ID;
            console.log(
                "ModelManager: Initializing. URL preselected model:",
                urlPreselectedModel
            );
            
            // Bare /lab always opens the Hall. The head guard clears this
            // before the bundle executes; ignore it here too so a stale value
            // can never win through script-order or storage-access races.
            const bareHallEntry = window.__LAB_EXPLICIT_ENTRY === false;
            const storedModelId = bareHallEntry
                ? null
                : localStorage.getItem("selectedModelId");
            if (bareHallEntry) {
                localStorage.removeItem("selectedModelId");
            }
            console.log(
                "ModelManager: localStorage selectedModelId:",
                storedModelId
            );

            if (storedModelId) {
                console.log(
                    "ModelManager: Using model from localStorage:",
                    storedModelId,
                );
                // If we have a localStorage model, that means it was just selected and
                // we should use it and update the server
                this.currentModelId = storedModelId;
                this.modelIdSource = "localStorage";
                console.log(
                    "ModelManager: Set currentModelId to:",
                    this.currentModelId
                );

                // Fire-and-forget server sync; chat reads character_id from
                // the request body, so the page never waits on this RTT.
                this.updateServerModel(storedModelId);

                // Clear from localStorage immediately (no need to wait for server)
                // But keep track if this was from a URL preselection
                localStorage.removeItem("selectedModelId");
                console.log("ModelManager: Cleared localStorage selectedModelId");
                
                // Store if this was from a URL preselection for proper comparison
                if (urlPreselectedModel === storedModelId) {
                    this.wasPreselectedFromUrl = true;
                }

                return;
            }

            // If no localStorage model, check the server for current model
            const response = await fetch("/api/models/current");
            if (!response.ok) {
                console.error(
                    "ModelManager: Failed to fetch current model from server",
                );
                return;
            }

            const { current_model } = await response.json();
            if (current_model) {
                console.log(
                    "ModelManager: Found model on server:",
                    current_model,
                );
                this.currentModelId = current_model;
                this.modelIdSource = "server";
                console.log(
                    "ModelManager: Set currentModelId to:",
                    this.currentModelId
                );
            } else {
                console.log("ModelManager: No model set on server");
                // If there's a URL preselected model but nothing on server, use it
                if (urlPreselectedModel) {
                    console.log(
                        "ModelManager: Using URL preselected model:",
                        urlPreselectedModel
                    );
                    this.currentModelId = urlPreselectedModel;
                    this.modelIdSource = "preselect";
                    await this.updateServerModel(urlPreselectedModel);
                }
            }
        } catch (error) {
            console.error("ModelManager: Error during initialization:", error);
        }
    }

    /**
     * Update the server's current model
     *
     * @param {string} modelId - The ID of the model to set as current on the server
     * @returns {Promise<boolean>} - True if successful, false otherwise
     */
    async updateServerModel(modelId) {
        try {
            console.log(
                "ModelManager: Updating server current model to:",
                modelId,
            );
            const response = await fetch("/api/models/current", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ model: modelId }),
            });

            if (!response.ok) {
                console.error(
                    "ModelManager: Failed to update server model:",
                    response.status,
                    response.statusText,
                );
                return false;
            } else {
                console.log("ModelManager: Server model updated successfully");
                return true;
            }
        } catch (error) {
            console.error("ModelManager: Error updating server model:", error);
            return false;
        }
    }

    /**
     * Set the current model - the central method for changing models
     *
     * @param {string} modelId - The ID of the model to set as current
     * @param {boolean} reloadPage - Whether to reload the page after setting the model
     * @returns {Promise<boolean>} - True if successful, false otherwise
     */
    async setCurrentModel(modelId, reloadPage = true) {
        console.log(
            "ModelManager: setCurrentModel called with:",
            modelId,
            "Current model:",
            this.currentModelId,
            "Comparison result:",
            modelId === this.currentModelId
        );
        
        if (modelId === this.currentModelId) {
            console.log(
                "ModelManager: Model already active, no need to switch",
            );
            return false;
        }

        // Set as current before any async operations
        this.currentModelId = modelId;

        // Notify listeners about model selection
        this.notifyModelSelectListeners(modelId);

        // No POST before navigation. The destination URL encodes the model,
        // chat stream reads character_id from each request body, and the
        // new page's initialize() will sync the server pointer.

        if (reloadPage) {
            // Store in localStorage temporarily to ensure the new page load picks it up
            // This will be cleared after the page loads and processes it
            localStorage.setItem("selectedModelId", modelId);
            console.log(
                "ModelManager: Set selectedModelId in localStorage for page transition:",
                modelId,
            );

            // Stash click timestamp for the next page's first-byte log.
            try {
                sessionStorage.setItem(
                    "characterClickAt",
                    String(performance.timeOrigin + performance.now()),
                );
            } catch (_) {}

            // CRITICAL: Navigate directly to the proper URL with the character slug
            // This ensures the URL matches the selected character and prevents race conditions
            const labUrl = getLabUrlForModel(modelId);
            console.log("ModelManager: Navigating directly to:", labUrl);

            // Use window.location.assign for a clean navigation that properly updates the URL
            // This is more reliable than window.location.href for ensuring the new URL is loaded
            window.location.assign(labUrl);

            // Return true immediately - the page will reload so no further code executes
            return true;
        } else {
            return true;
        }
    }

    /**
     * Get the current model ID
     *
     * @returns {string|null} - The current model ID or null if none is set
     */
    getCurrentModelId() {
        return this.currentModelId;
    }

    /**
     * Add a listener to be notified when a model is selected
     *
     * @param {Function} listener - A function that receives the model ID
     */
    addModelSelectListener(listener) {
        if (
            typeof listener === "function" &&
            !this.modelSelectListeners.includes(listener)
        ) {
            this.modelSelectListeners.push(listener);
        }
    }

    /**
     * Remove a model select listener
     *
     * @param {Function} listener - The listener to remove
     */
    removeModelSelectListener(listener) {
        const index = this.modelSelectListeners.indexOf(listener);
        if (index !== -1) {
            this.modelSelectListeners.splice(index, 1);
        }
    }

    /**
     * Notify all model select listeners
     *
     * @param {string} modelId - The ID of the model that was selected
     */
    notifyModelSelectListeners(modelId) {
        for (const listener of this.modelSelectListeners) {
            try {
                listener(modelId);
            } catch (error) {
                console.error(
                    "ModelManager: Error in model select listener:",
                    error,
                );
            }
        }
    }

    /**
     * Add a listener to be notified when a model is loaded
     *
     * @param {Function} listener - A function that receives the model ID
     */
    addModelLoadListener(listener) {
        if (
            typeof listener === "function" &&
            !this.modelLoadListeners.includes(listener)
        ) {
            this.modelLoadListeners.push(listener);
        }
    }

    /**
     * Remove a model load listener
     *
     * @param {Function} listener - The listener to remove
     */
    removeModelLoadListener(listener) {
        const index = this.modelLoadListeners.indexOf(listener);
        if (index !== -1) {
            this.modelLoadListeners.splice(index, 1);
        }
    }

    /**
     * Notify the system that a model has been loaded
     *
     * @param {string} modelId - The ID of the model that was loaded
     */
    notifyModelLoaded(modelId) {
        this.isModelLoading = false;

        for (const listener of this.modelLoadListeners) {
            try {
                listener(modelId);
            } catch (error) {
                console.error(
                    "ModelManager: Error in model load listener:",
                    error,
                );
            }
        }
    }
}
